import { useState } from 'react';
import { CANCEL_REASONS, REJECT_REASONS } from './constants';

type ModalAction = 'cancel' | 'reject';

interface CancelRejectModalProps {
  readonly orderId: string;
  readonly onConfirm: (action: ModalAction, reason: string) => void;
  readonly onClose: () => void;
  readonly isPending: boolean;
}

export const CancelRejectModal = ({
  orderId,
  onConfirm,
  onClose,
  isPending,
}: CancelRejectModalProps) => {
  const [action, setAction] = useState<ModalAction>('cancel');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  const reasons = action === 'cancel' ? CANCEL_REASONS : REJECT_REASONS;
  const shortId = orderId.slice(-8).toUpperCase();

  const fullReason = notes.trim()
    ? `${reason}${reason ? ' - ' : ''}${notes.trim()}`
    : reason;

  const handleConfirm = () => {
    if (!reason) return;
    onConfirm(action, fullReason);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: '20px 24px',
          width: '360px',
          maxWidth: '90%',
          boxShadow: 'var(--shadow-lg)',
        }}
      >
        <h3
          style={{
            fontSize: '16px',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text-primary)',
            marginBottom: '4px',
          }}
        >
          {action === 'cancel' ? 'Cancel order' : 'Reject order'}
        </h3>
        <p
          style={{
            fontSize: '13px',
            color: 'var(--text-secondary)',
            marginBottom: '14px',
          }}
        >
          This will {action} #{shortId} and can trigger a refund. This action cannot be undone.
        </p>

        {/* Cancel/Reject toggle */}
        <div
          className="flex"
          style={{
            gap: 0,
            marginBottom: '12px',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          <ToggleButton
            active={action === 'cancel'}
            onClick={() => { setAction('cancel'); setReason(''); }}
            label="Cancel"
          />
          <ToggleButton
            active={action === 'reject'}
            onClick={() => { setAction('reject'); setReason(''); }}
            label="Reject"
          />
        </div>

        {/* Reason dropdown */}
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="admin-input"
          style={{ width: '100%', marginBottom: '8px', fontSize: '13px' }}
        >
          <option value="">Select reason...</option>
          {reasons.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>

        {/* Notes */}
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Additional details (optional)..."
          className="admin-input"
          style={{ width: '100%', minHeight: '70px', fontSize: '13px', resize: 'vertical', marginBottom: '12px' }}
        />

        {/* Footer */}
        <div className="flex justify-end" style={{ gap: '8px' }}>
          <button
            onClick={onClose}
            style={{
              fontSize: '13px',
              padding: '7px 16px',
              minHeight: '44px',
              borderRadius: 'var(--radius-md)',
              border: '0.5px solid var(--border-default)',
              backgroundColor: 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >
            Keep order
          </button>
          <button
            onClick={handleConfirm}
            disabled={!reason || isPending}
            style={{
              fontSize: '13px',
              padding: '7px 16px',
              minHeight: '44px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              backgroundColor: 'var(--bg-error-subtle)',
              color: 'var(--color-error-600)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: !reason || isPending ? 'not-allowed' : 'pointer',
              opacity: !reason || isPending ? 0.6 : 1,
            }}
          >
            {isPending ? 'Processing...' : action === 'cancel' ? 'Cancel order' : 'Reject order'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ToggleButton = ({
  active,
  onClick,
  label,
}: {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly label: string;
}) => (
  <button
    onClick={onClick}
    style={{
      flex: 1,
      padding: '6px',
      minHeight: '44px',
      fontSize: '13px',
      fontWeight: active ? 'var(--font-weight-medium)' : 'var(--font-weight-regular)',
      border: 'none',
      backgroundColor: active ? 'var(--bg-surface-sunken)' : 'transparent',
      color: active ? 'var(--text-primary)' : 'var(--text-tertiary)',
      cursor: 'pointer',
      transition: 'all 100ms',
    }}
  >
    {label}
  </button>
);
